// Data cache service for progressions with smart refresh logic
//
// ARCHITECTURE OPTIMIZATION (2025-06-20):
// Standard progressions are now optimized - returned directly as static imports
// instead of unnecessary triple-caching (static -> session -> localStorage).
// This eliminates ~200 lines of complex caching logic for immutable data while
// maintaining the abstraction for future Supabase migration.
//
// CACHING STRATEGY:
// - Standard Progressions: Direct static import (no caching overhead)
// - User Progressions: Smart dual-layer caching (session + localStorage)
//
// FUTURE DB MIGRATION:
// When standard progressions move to Supabase, simply replace 
// getCachedStandardProgressions() implementation - API stays the same.

import type { Progression } from '../presets/presetSchema';
import { STANDARD_PROGRESSIONS, STANDARD_PROGRESSIONS_VERSION } from '../presets/standardProgressions';
import { getUserProgressionsFromDB } from './dataService';
import { getCurrentUser } from '../auth/supabaseAuthService';

// Cache configuration
const CACHE_CONFIG = {
  USER_PROGRESSIONS_KEY: 'cached_user_progressions',
  STANDARD_PROGRESSIONS_KEY: 'cached_standard_progressions', 
  STANDARD_PROGRESSIONS_VERSION_KEY: 'standard_progressions_version',
  USER_CACHE_DURATION: 5 * 60 * 1000, // 5 minutes for user progressions
  STANDARD_CACHE_DURATION: 24 * 60 * 60 * 1000, // 24 hours for standard progressions
  LAST_FETCH_KEY: 'last_fetch_timestamp',
  USER_LAST_FETCH_KEY: 'user_last_fetch_timestamp',
  CACHE_BUST_KEY: 'cache_bust_timestamp'
};

interface CachedData<T> {
  data: T;
  timestamp: number;
  userId?: string; // For user-specific caches
}

interface ProgressionCache {
  standard: Progression[];
  user: Progression[];
  lastUpdated: {
    standard: number;
    user: number;
  };
}

// In-memory cache for the current session
let sessionCache: ProgressionCache = {
  standard: [],
  user: [],
  lastUpdated: {
    standard: 0,
    user: 0
  }
};

/**
 * Get cached data from localStorage if valid
 */
function getCachedData<T>(key: string, maxAge: number, userId?: string): T | null {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const parsedCache: CachedData<T> = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is expired
    if (now - parsedCache.timestamp > maxAge) {
      localStorage.removeItem(key);
      return null;
    }

    // For user-specific caches, check if the userId matches
    if (userId && parsedCache.userId !== userId) {
      localStorage.removeItem(key);
      return null;
    }

    return parsedCache.data;
  } catch {
    // Clear invalid cache data
    localStorage.removeItem(key);
    return null;
  }
}

/**
 * Save data to localStorage cache
 */
function setCachedData<T>(key: string, data: T, userId?: string): void {
  try {
    const cacheData: CachedData<T> = {
      data,
      timestamp: Date.now(),
      userId
    };
    localStorage.setItem(key, JSON.stringify(cacheData));
  } catch {
    // Silently handle localStorage errors (quota exceeded, etc.)
  }
}

/**
 * Get standard progressions with optimized caching
 * 
 * OPTIMIZATION: Standard progressions are static imports, already "cached" in memory.
 * No need for localStorage/sessionCache since they're immutable and bundled with the app.
 * This maintains the abstraction for future DB migration while eliminating over-caching.
 */
export async function getCachedStandardProgressions(): Promise<Progression[]> {
  // OPTIMIZATION: Just return the static data - it's already "cached" as an import
  // When we move to DB, we can re-enable full caching by replacing this implementation
  return [...STANDARD_PROGRESSIONS];
}

/**
 * Get user progressions with caching and smart refresh
 */
export async function getCachedUserProgressions(forceRefresh: boolean = false): Promise<Progression[]> {
  try {
    const user = await getCurrentUser();
    if (!user) {
      return [];
    }

    const now = Date.now();
    const userId = user.uid;

    // Check if we need to force refresh
    if (!forceRefresh) {
      // Check session cache first
      if (sessionCache.user.length > 0 && 
          now - sessionCache.lastUpdated.user < CACHE_CONFIG.USER_CACHE_DURATION) {
        return sessionCache.user;
      }

      // Check localStorage cache
      const cached = getCachedData<Progression[]>(
        CACHE_CONFIG.USER_PROGRESSIONS_KEY, 
        CACHE_CONFIG.USER_CACHE_DURATION,
        userId
      );
      
      if (cached) {
        // Update session cache
        sessionCache.user = cached;
        sessionCache.lastUpdated.user = now;
        return cached;
      }
    }

    // Fetch fresh data from database
    const result = await getUserProgressionsFromDB(userId);
    const userProgressions = result.success ? (result.data || []) : [];
    
    // Cache the data
    setCachedData(CACHE_CONFIG.USER_PROGRESSIONS_KEY, userProgressions, userId);
    sessionCache.user = userProgressions;
    sessionCache.lastUpdated.user = now;
    
    return userProgressions;
  } catch {
    // Return empty array on error, but preserve any cached data
    return sessionCache.user;
  }
}

/**
 * Get all progressions (standard + user) with intelligent caching
 * 
 * OPTIMIZATION: Standard progressions are now returned directly (no caching overhead),
 * while user progressions use smart caching where it actually provides value.
 */
export async function getAllCachedProgressions(forceRefreshUser: boolean = false): Promise<{
  standard: Progression[],
  user: Progression[],
  fromCache: { standard: boolean, user: boolean }
}> {
  const [standardProgressions, userProgressions] = await Promise.all([
    getCachedStandardProgressions(), // Now optimized - returns static data directly
    getCachedUserProgressions(forceRefreshUser)
  ]);

  const now = Date.now();
  
  return {
    standard: standardProgressions,
    user: userProgressions,
    fromCache: {
      standard: true, // Always "cached" since it's static data
      user: now - sessionCache.lastUpdated.user < CACHE_CONFIG.USER_CACHE_DURATION && !forceRefreshUser
    }
  };
}

/**
 * Invalidate user progressions cache (call after save/delete operations)
 */
export async function invalidateUserProgressionsCache(): Promise<void> {
  try {
    const user = await getCurrentUser();
    if (user) {
      localStorage.removeItem(CACHE_CONFIG.USER_PROGRESSIONS_KEY);
      sessionCache.user = [];
      sessionCache.lastUpdated.user = 0;
    }
  } catch {
    // Silently handle errors
  }
}

/**
 * Invalidate standard progressions cache 
 * 
 * OPTIMIZATION: Since standard progressions are now returned directly as static data,
 * this function mainly cleans up any old cache entries for migration/debugging purposes.
 */
export function invalidateStandardProgressionsCache(): void {
  // Clean up any old cache entries (for migration from previous caching approach)
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith(CACHE_CONFIG.STANDARD_PROGRESSIONS_KEY)) {
      localStorage.removeItem(key);
    }
  });
  localStorage.removeItem(CACHE_CONFIG.STANDARD_PROGRESSIONS_VERSION_KEY);
  
  // Clear session cache (though not used for standard progressions anymore)
  sessionCache.standard = [];
  sessionCache.lastUpdated.standard = 0;
}

/**
 * Invalidate all caches (useful for debugging or major updates)
 */
export function invalidateAllCaches(): void {
  localStorage.removeItem(CACHE_CONFIG.STANDARD_PROGRESSIONS_KEY);
  localStorage.removeItem(CACHE_CONFIG.USER_PROGRESSIONS_KEY);
  localStorage.removeItem(CACHE_CONFIG.STANDARD_PROGRESSIONS_VERSION_KEY);
  sessionCache = {
    standard: [],
    user: [],
    lastUpdated: { standard: 0, user: 0 }
  };
}

/**
 * Force refresh standard progressions cache
 * 
 * OPTIMIZATION: Since standard progressions are now static data, this just returns
 * them directly. Kept for API compatibility and potential future DB migration.
 */
export async function forceRefreshStandardProgressions(): Promise<Progression[]> {
  // Clear any old cache entries for cleanup
  invalidateStandardProgressionsCache();
  
  // Return static data directly (no caching needed)
  return [...STANDARD_PROGRESSIONS];
}

/**
 * Get cache status for debugging/UI purposes
 * 
 * OPTIMIZATION: Updated to reflect that standard progressions are now static data.
 */
export function getCacheStatus(): {
  standard: { cached: boolean, age: number, version: string | null },
  user: { cached: boolean, age: number }
} {
  const now = Date.now();
  return {
    standard: {
      cached: true, // Always "cached" since it's static data
      age: 0, // Always fresh since it's static
      version: STANDARD_PROGRESSIONS_VERSION // Current version from import
    },
    user: {
      cached: sessionCache.user.length > 0,
      age: now - sessionCache.lastUpdated.user
    }
  };
}

/**
 * Debug function to inspect cache contents (for console debugging)
 */
export function debugCache(): {
  status: ReturnType<typeof getCacheStatus>,
  expectedVersion: string,
  sessionCacheInfo: {
    standardCount: number,
    userCount: number,
    lastUpdated: { standard: number, user: number }
  },
  cacheKeys: string[]
} {
  const status = getCacheStatus();
  const cacheKeys = Object.keys(localStorage).filter(key => 
    key.includes('progression') || key.includes('standard')
  );
  
  const result = {
    status,
    expectedVersion: STANDARD_PROGRESSIONS_VERSION,
    sessionCacheInfo: {
      standardCount: sessionCache.standard.length,
      userCount: sessionCache.user.length,
      lastUpdated: sessionCache.lastUpdated
    },
    cacheKeys
  };
  
  // Only log in development
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log('Cache Debug Info:', result);
  }
  
  return result;
}

/**
 * Preload caches on app startup
 * 
 * OPTIMIZATION: Now only preloads user progressions since standard progressions
 * are static data and don't need preloading.
 */
export async function preloadCaches(): Promise<void> {
  try {
    // Only preload user progressions - standard progressions are static imports
    await getCachedUserProgressions();
  } catch {
    // Silently handle preload errors
  }
}

// Export debugging utilities
if (import.meta.env.DEV) {
  // Make functions available on window for console debugging
  (window as any).fretCacheDebug = {
    debugCache,
    forceRefreshStandardProgressions,
    invalidateAllCaches,
    getCacheStatus
  };
}
